import { DateTime } from 'luxon'

import { SAT_BOOTCAMP_ONBOARDING_TOPIC } from '@/queries/constants'
import SATTestInterventionArmQ from '@/queries/SATTestInterventionArmQ'
import SATTestInterventionArmTaskQ from '@/queries/SATTestInterventionArmTaskQ'
import SATTestQ from '@/queries/SATTestQ'
import SATUserQ from '@/queries/SATUserQ'
import SeriesQ from '@/queries/SeriesQ'
import SeriesTagQ from '@/queries/SeriesTagQ'
import SessionHostQ from '@/queries/SessionHostQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import { type Query, SQL, type SQLFragment } from '@/client/query'
import { SeriesTag } from '@/client/services/tags/types'
import { LevelRequirementStatus } from '@/client/services/worlds/progression/enum'

import type {
  SATAssessmentsVersion,
  SATBootcampInterventionArmDetails,
  SATBootcampInterventionTaskDetails,
} from './types'

export function satBootcampInterventionTaskDetailsFromQuery(
  task: Query
): SATBootcampInterventionTaskDetails {
  const dueDate = task(SATTestInterventionArmTaskQ.due_date)
  return {
    id: task(SATTestInterventionArmTaskQ.id) ?? 0,
    name: task(SATTestInterventionArmTaskQ.name) ?? '',
    releaseDate: DateTime.fromISO(`${task(SATTestInterventionArmTaskQ.release_date)}`),
    dueDate: dueDate == null ? null : DateTime.fromISO(dueDate),
    link: task(SATTestInterventionArmTaskQ.link) ?? '',
    satTestId: task(SATTestInterventionArmQ.sat_test_id) ?? 0,
    satTestInterventionArmId: task(SATTestInterventionArmTaskQ.sat_test_intervention_arm_id) ?? 0,
    reminderHistory: (task(SATTestInterventionArmTaskQ.reminder_history) ?? []).map(
      reminderTimestamp => DateTime.fromISO(reminderTimestamp)
    ),
    forLearners: task(SATTestInterventionArmTaskQ.for_learners) ?? false,
  }
}

export function satBootcampInterventionArmFromQuery(
  interventionArm: Query
): SATBootcampInterventionArmDetails {
  const tasks = interventionArm.many(
    SATTestInterventionArmTaskQ.where({
      satTestInterventionArmId: SATTestInterventionArmQ.id,
      order: [[SATTestInterventionArmTaskQ.release_date, 'asc']],
    })
  )
  return {
    id: interventionArm(SATTestInterventionArmQ.id) ?? 0,
    name: interventionArm(SATTestInterventionArmQ.name) ?? '',
    group: interventionArm(SATTestInterventionArmQ.group_slug) ?? '',
    userType: interventionArm(SATTestInterventionArmQ.user_type) ?? 'learner',
    weight: interventionArm(SATTestInterventionArmQ.weight) ?? 0,
    satTestTitle: interventionArm(SATTestQ.title) ?? '',
    countUsers: interventionArm(SATTestInterventionArmQ.countUsers()) ?? 0,
    createdAt: DateTime.fromISO(`${interventionArm(SATTestInterventionArmQ.created_at)}`),
    tasks: tasks.map(satBootcampInterventionTaskDetailsFromQuery),
  }
}

// December 15 2023
const DIGITAL_SAT_V1_TRANSITION_DATE = DateTime.local(2023, 12, 15).toJSDate()

// September 1, 2025
const DIGITAL_SAT_V2_TRANSITION_DATE = DateTime.local(2025, 9, 1).toJSDate()

export function getSATAssessmentsVersion(seriesStartDate: Date): SATAssessmentsVersion {
  if (seriesStartDate > DIGITAL_SAT_V2_TRANSITION_DATE) {
    return 'dsat-2'
  }
  if (seriesStartDate > DIGITAL_SAT_V1_TRANSITION_DATE) {
    return 'dsat-1'
  }

  return 'original'
}

export function getOnboardingSessionHostSoftDeadline({
  onboardingSessionDeadline,
  tutorRegistrationDeadline,
}: {
  onboardingSessionDeadline: DateTime
  tutorRegistrationDeadline: DateTime
}): DateTime {
  return tutorRegistrationDeadline > DateTime.now()
    ? tutorRegistrationDeadline
    : onboardingSessionDeadline
}

const EARLY_REGISTRATION_DEADLINE_DAYS_FROM_OPEN = 6

export function getEarlyTutorRegistrationDeadline({
  tutorRegistrationOpens,
}: {
  tutorRegistrationOpens: DateTime
}): DateTime {
  return tutorRegistrationOpens.plus({ days: EARLY_REGISTRATION_DEADLINE_DAYS_FROM_OPEN })
}

export function canTutorSATOnboardingSession({
  userId,
}: {
  userId: number | SQLFragment<number>
}): SQLFragment<boolean> {
  return SQL<boolean>`(${getOnboardingSessionEligibilityStatus({ userId })} != ${LevelRequirementStatus.Locked})`
}

export function getOnboardingSessionEligibilityStatus({
  userId,
}: {
  userId: number | SQLFragment<number>
}): SQLFragment<LevelRequirementStatus> {
  return SQL<LevelRequirementStatus>`
    (
      CASE WHEN ${SessionHostQ.hasTutoredSessionWithTopicAndAtLeastOneLearner({
        userId,
        topicName: SAT_BOOTCAMP_ONBOARDING_TOPIC,
      })}
        THEN ${LevelRequirementStatus.Complete}
        WHEN EXISTS(SELECT 1 ${SessionQ.where(
          {
            tutor: userId,
            include: [
              {
                association: 'Topic',
                required: true,
                where: SQL`${TopicQ.name} = ${SAT_BOOTCAMP_ONBOARDING_TOPIC}`,
              },
            ],
          },
          SQL`${SessionQ.start_date} > NOW()`
        )})
        THEN ${LevelRequirementStatus.InProgress}
        WHEN NOT EXISTS(SELECT 1 ${SeriesTagQ.where({
          tag: SeriesTag.HighlyEngagingSATBootcamp,
          include: [
            {
              association: 'Series',
              required: true,
              where: SQL`${SeriesQ.user_id} = ${userId}`,
            },
          ],
        })})
        AND NOT EXISTS(SELECT 1 ${SATUserQ.where(
          { userId },
          SATUserQ.hasEngagingBootcampRequirementWaived()
        )})
        THEN ${LevelRequirementStatus.Locked}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
  `
}
